import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail, Key, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code + new password, 3: success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Controlla la tua email per il codice di recupero");
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: code,
        new_password: newPassword,
      });
      toast.success("Password modificata con successo!");
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Codice non valido o scaduto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(to bottom right, rgba(214, 64, 69, 0.1), rgba(248, 249, 250, 1))`,
      }}
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna al login
        </Link>

        <Card className="border-0 shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
                <span className="text-white font-heading font-black text-2xl">D</span>
              </div>
              <span className="text-secondary font-heading font-bold text-xl tracking-tight">
                DRAKE TENNIS
              </span>
            </div>
            <CardTitle className="font-heading text-3xl font-bold uppercase tracking-tight">
              {step === 1 && "Recupera Password"}
              {step === 2 && "Nuova Password"}
              {step === 3 && "Fatto!"}
            </CardTitle>
            <CardDescription>
              {step === 1 && "Inserisci la tua email per ricevere il codice di recupero"}
              {step === 2 && "Inserisci il codice ricevuto via email e la nuova password"}
              {step === 3 && "La tua password è stata modificata"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <form onSubmit={handleRequestReset} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium uppercase tracking-wide">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="mario@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 border-2 focus:border-primary"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]"
                  disabled={loading}
                  data-testid="request-reset-btn"
                >
                  {loading ? (
                    "Invio in corso..."
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Invia Codice
                    </>
                  )}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-sm font-medium uppercase tracking-wide">
                    Codice di Recupero
                  </Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="XXXXXXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    required
                    maxLength={8}
                    className="h-12 border-2 focus:border-primary text-center font-mono text-lg tracking-widest"
                    data-testid="reset-code-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Controlla l'email inviata a {email}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium uppercase tracking-wide">
                    Nuova Password
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 border-2 focus:border-primary"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium uppercase tracking-wide">
                    Conferma Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 border-2 focus:border-primary"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    "Modifica in corso..."
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Modifica Password
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setStep(1)}
                >
                  Richiedi nuovo codice
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <p className="text-muted-foreground">
                  Puoi ora accedere con la tua nuova password
                </p>
                <Button
                  className="w-full h-12 font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]"
                  onClick={() => navigate("/login")}
                  data-testid="go-to-login-btn"
                >
                  Vai al Login
                </Button>
              </div>
            )}

            {step !== 3 && (
              <div className="mt-6 text-center">
                <p className="text-muted-foreground text-sm">
                  Ricordi la password?{" "}
                  <Link 
                    to="/login" 
                    className="text-primary font-semibold hover:underline"
                  >
                    Accedi
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
